import React, { useEffect, useRef, useState } from "react";
import { authedHttp } from "@/lib/session";
import { RemoveButton } from "./Fields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = authedHttp(API);

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const MicIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;

export function VoiceNotes({ configId, q, answer, onAnswer, onTranscript, locked }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | error
  const [error, setError] = useState("");
  const [secs, setSecs] = useState(0);
  const [playing, setPlaying] = useState(null);
  const rec = useRef(null);
  const chunks = useRef([]);
  const tick = useRef(null);
  const audio = useRef(null);
  const secsRef = useRef(0);
  const notes = (answer?.voice_notes || []).filter((n) => !n.is_deleted);
  const supported = typeof window !== "undefined" && !!window.MediaRecorder && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => () => { clearInterval(tick.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); audio.current?.pause(); }, []);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = () => upload(new Blob(chunks.current, { type: mr.mimeType || mime || "audio/webm" }));
      mr.start(250);
      rec.current = mr;
      setSecs(0); secsRef.current = 0;
      tick.current = setInterval(() => { secsRef.current += 1; setSecs(secsRef.current); }, 1000);
      setState("recording");
    } catch (e) {
      setState("error");
      setError(e?.name === "NotAllowedError" ? "Microphone access was blocked. Allow it in your browser and try again." : "Could not start recording on this device.");
    }
  };

  const stop = () => {
    clearInterval(tick.current);
    const mr = rec.current;
    if (!mr) return;
    mr.stream.getTracks().forEach((t) => t.stop());
    if (mr.state !== "inactive") mr.stop();
    setState("uploading");
  };

  const upload = async (blob) => {
    const duration = secsRef.current;
    try {
      const form = new FormData();
      form.append("file", blob, `note.${(blob.type.split("/")[1] || "webm").split(";")[0]}`);
      form.append("duration", String(duration));
      const r = await http.post(`/onboarding/${configId}/voice/${q.id}`, form);
      onAnswer(r.data.answer);
      if (r.data.note.transcript && onTranscript) onTranscript(r.data.note.transcript);
      setState("idle");
    } catch (e) {
      setState("error");
      setError(e?.response?.data?.detail || "The recording could not be saved. Please try again.");
    }
  };

  const remove = async (id) => {
    const r = await http.delete(`/onboarding/${configId}/voice/${id}`);
    onAnswer(r.data.answer);
  };

  const play = (id) => {
    if (playing === id) { audio.current?.pause(); setPlaying(null); return; }
    audio.current?.pause();
    const a = new Audio(`${API}/onboarding/${configId}/voice/${id}`);
    a.crossOrigin = "use-credentials";
    a.onended = () => setPlaying(null);
    audio.current = a;
    a.play().catch(() => setPlaying(null));
    setPlaying(id);
  };

  if (!supported) return null;

  return (
    <div className="fo-voice" data-testid="voice-module">
      <div className="fo-voice-bar">
        {state === "recording" ? (
          <>
            <span className="fo-rec-dot" aria-hidden />
            <span className="num" data-testid="voice-timer">{fmt(secs)}</span>
            <span className="muted">Listening. Speak naturally; Foundation will transcribe it.</span>
            <button type="button" className="fo-btn sm" onClick={stop} data-testid="voice-stop">Stop and save</button>
          </>
        ) : state === "uploading" ? (
          <>
            <span className="fo-spin sm" aria-hidden />
            <span className="muted" data-testid="voice-transcribing">Saving and transcribing your recording…</span>
          </>
        ) : (
          <>
            <button type="button" className="fo-btn2 sm" onClick={start} disabled={locked} data-testid="voice-start"><MicIcon /> Speak your answer</button>
            <span className="muted">Prefer to talk? Record it and we save the audio and the transcript{onTranscript ? ", added to your answer below" : " alongside your answer"}.</span>
          </>
        )}
      </div>
      {state === "error" && <p className="fo-voice-err" data-testid="voice-error">{error}</p>}
      {notes.length > 0 && (
        <ul className="fo-notes" data-testid="voice-notes">
          {notes.map((n, i) => (
            <li className="fo-note" key={n.id} data-testid={`voice-note-${i}`}>
              <button type="button" className={`fo-play ${playing === n.id ? "on" : ""}`} onClick={() => play(n.id)} aria-label={playing === n.id ? "Pause" : "Play"} data-testid={`voice-play-${i}`}>
                {playing === n.id ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg> : <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4l12 8-12 8z" /></svg>}
              </button>
              <div className="fo-note-body">
                <span className="fo-note-meta">Voice note · {fmt(n.duration || 0)}</span>
                <span className="fo-note-text" data-testid={`voice-transcript-${i}`}>{n.transcript || (n.transcription_error ? "Saved. Transcription did not complete; Crowther will listen to the recording." : "Saved. Nothing was heard clearly; try again a little closer to the microphone.")}</span>
              </div>
              {!locked && <RemoveButton onClick={() => remove(n.id)} label="Remove voice note" testId={`voice-remove-${i}`} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
